import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  ConsultOrderProvider,
  useConsultOrderContext,
} from "../../contexts/ConsultOrderContext";
import {
  ConsultOrderPhase,
  useConsultOrderLifecycle,
} from "../../hooks/useConsultOrderLifecycle";
import {
  ConsultDialogKind,
  videoConsultActions,
} from "../../store/videoConsult-slice";
import { ConsultCallerUser } from "../../types/consultOrder.types";
import CreateOrderChip from "./CreateOrderChip";
import ConsultOrderPromptModal, {
  PromptRfzoState,
} from "./ConsultOrderPromptModal";
import ConsultOrderFormModal from "./ConsultOrderFormModal";
import ConsultRfzoFormModal from "./ConsultRfzoFormModal";

// App-level, route-independent host for the consult-order flow, a sibling of
// <VideoConsultManager> in App.tsx. It NEVER lives inside <VideoConsultAlert>
// (that returns null when the list is empty and re-mounts).
//
// The provider wraps an inner host so the lifecycle hook (which reads the draft
// on submit) can consume the same context the form components write to.

/** Friendly caller display name for the chip + confirmation copy. */
export const callerDisplayName = (
  user: ConsultCallerUser | null | undefined,
): string => {
  if (!user) {
    return "korisnika";
  }
  const full = `${user.firstName ?? ""} ${user.secondName ?? ""}`.trim();
  return full || user.username || "korisnika";
};

/**
 * The consult dialog a lifecycle phase puts on screen: the prompt, the
 * reservation form (its submitting and result views included) or the RFZO
 * form (likewise), or none. The single source for which modal is open.
 */
export const openConsultDialogFor = (
  phase: ConsultOrderPhase,
): ConsultDialogKind | null => {
  switch (phase) {
    case "PROMPT":
      return "PROMPT";
    case "FORM":
    case "SUBMITTING":
    case "SUCCESS":
      return "ORDER";
    case "RFZO_FORM":
    case "RFZO_SUBMITTING":
    case "RFZO_RESULT":
      return "RFZO";
    default:
      return null;
  }
};

const ConsultOrderHost: React.FC = () => {
  const dispatch = useDispatch();
  const { prefill: ctxPrefill } = useConsultOrderContext();
  const life = useConsultOrderLifecycle();

  // The prompt/chip use the hook-local prefill (available before hydration);
  // once hydrated, the context prefill is the same object.
  const caller = life.prefill?.user ?? ctxPrefill?.user ?? null;
  const callerName = callerDisplayName(caller);

  const openDialog = openConsultDialogFor(life.phase);
  const formOpen = openDialog === "ORDER";
  const rfzoModalOpen = openDialog === "RFZO";

  // The floating call panel lives outside this provider (it collapses to a
  // bar on phones while a consult dialog is open), so the open dialog is
  // published to the videoConsult slice: the kind only, never draft data.
  useEffect(() => {
    dispatch(videoConsultActions.setOpenConsultDialog(openDialog));
  }, [dispatch, openDialog]);
  // A remount (persona key) or logout unmounts the host with a dialog open.
  useEffect(
    () => () => {
      dispatch(videoConsultActions.setOpenConsultDialog(null));
    },
    [dispatch],
  );

  // ITAM-2887: every RFZO result is terminal (the advisor only raises an
  // empty request; the pharmacy takes it from there).
  const rfzoState: PromptRfzoState =
    life.rfzoResult == null ? "NONE" : "TERMINAL";

  return (
    <>
      {life.chipVisible && (
        <CreateOrderChip callerName={callerName} onOpen={life.openPrompt} />
      )}

      <ConsultOrderPromptModal
        isOpen={openDialog === "PROMPT"}
        callerName={callerName}
        cncFormVisited={life.cncFormVisited}
        rfzoFormVisited={life.rfzoFormVisited}
        rfzoState={rfzoState}
        onConfirmOrder={life.confirmPrompt}
        onConfirmRfzo={life.confirmRfzo}
        onResumeRfzo={life.resumeRfzo}
        onClose={life.dismissPrompt}
      />

      <ConsultOrderFormModal
        isOpen={formOpen}
        phase={life.phase}
        graceActive={life.graceActive}
        consultEnded={life.consultEnded}
        createdOrder={life.createdOrder}
        submitError={life.submitError}
        validationErrors={life.validationErrors}
        submitAttemptId={life.submitAttemptId}
        lineErrors={life.lineErrors}
        onSubmit={life.submit}
        onClose={life.closeForm}
        onCloseSuccess={life.closeSuccess}
        onDismissError={life.dismissSubmitError}
      />

      <ConsultRfzoFormModal
        isOpen={rfzoModalOpen}
        phase={life.phase}
        graceActive={life.graceActive}
        consultEnded={life.consultEnded}
        rfzoResult={life.rfzoResult}
        submitError={life.rfzoSubmitError}
        validationErrors={life.rfzoValidationErrors}
        submitAttemptId={life.rfzoSubmitAttemptId}
        onSubmit={life.submitRfzo}
        onClose={life.closeRfzoForm}
        onCloseResult={life.closeRfzoResult}
        onDismissError={life.dismissRfzoSubmitError}
      />
    </>
  );
};

const ConsultOrderManager: React.FC = () => {
  return (
    <ConsultOrderProvider>
      <ConsultOrderHost />
    </ConsultOrderProvider>
  );
};

export default ConsultOrderManager;
